import { useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import { useLocation } from 'react-router'
import { Input } from '../../common/html/input'
import { EventCell } from '../../common/news/EventCell'
import { Pagination } from '../../common/pagination'
import { Table } from '../../common/table'
import { TextEllipsisStaticLine } from '../../common/textEllipsis/TextEllipsisStaticLine'
import { debounce } from '../../helpers/common'
import { FORMAT, formatDateTime } from '../../helpers/datetime'
import { BookmarkCell } from './common/table/BookmarkCell'
import { FilterTableBody } from './common/table/FilterTableBody'
import { StatusCell } from './common/table/StatusCell'
import { HEADER_TITLE } from './constants'
import { getCheckedIdsOfEvents } from './helpers'
import {
  changeIds,
  changePage,
  changePageSize,
  changeSearchText,
  selectEndDate,
  selectIds,
  selectIsBookmark,
  selectIsLoading,
  selectLanguages,
  selectNewsType,
  selectPage,
  selectPageSize,
  selectSearchText,
  selectSentiments,
  selectStartDate,
  selectTableItem,
  selectTotalRecords,
} from './store/slice'
import { deleteBookmark, getNewsAndSocials, postBookmark } from './store/thunk'

export const FilterResult = () => {
  const dispatch = useDispatch()
  const location = useLocation()

  const locale = useSelector((state) => state.i18n.locale)
  const isLoading = useSelector(selectIsLoading)
  const isBookmark = useSelector(selectIsBookmark)
  const totalRecords = useSelector(selectTotalRecords)
  const page = useSelector(selectPage)
  const pageSize = useSelector(selectPageSize)
  const ids = useSelector(selectIds)
  const sentiments = useSelector(selectSentiments)
  const searchText = useSelector(selectSearchText)
  const startDate = useSelector(selectStartDate)
  const endDate = useSelector(selectEndDate)
  const newsTypes = useSelector(selectNewsType)
  const languages = useSelector(selectLanguages)

  const { organizationId, newsId } = new Proxy(
    new URLSearchParams(location.search),
    {
      get: (searchParam, prop) => searchParam.get(prop),
    },
  )

  const onRowClick = (rowId) => {
    const indexOfRowId = ids.indexOf(rowId)
    if (indexOfRowId !== -1) {
      if (ids.every((id) => id !== `${rowId}-shortContent`)) {
        const newIds = [
          ...ids.slice(0, indexOfRowId + 1),
          `${rowId}-shortContent`,
          ...ids.slice(indexOfRowId + 1),
        ]
        dispatch(changeIds(newIds))
      } else {
        const newIds = ids.filter((id) => id !== `${rowId}-shortContent`)
        dispatch(changeIds(newIds))
      }
    }
  }

  const params = {
    organizationId,
    page,
    pageSize,
    isBookMarked: isBookmark,
    newsTypeIds: sentiments
      .filter((item) => item.isChecked)
      ?.map((item) => item.newsTypeId),
    newsCategoryIds: getCheckedIdsOfEvents(newsTypes),
    newsLanguages: languages
      .filter((item) => item.isChecked)
      .map((item) => item.value),
    term: searchText,
    startDate,
    endDate,
  }

  useEffect(() => {
    dispatch(changePage(1))
  }, [
    pageSize,
    isBookmark,
    searchText,
    startDate,
    endDate,
    sentiments,
    newsTypes,
    languages,
  ])

  useEffect(() => {
    dispatch(getNewsAndSocials(params))
  }, [
    page,
    pageSize,
    locale,
    isBookmark,
    searchText,
    startDate,
    endDate,
    sentiments,
    newsTypes,
    languages,
  ])

  useEffect(() => {
    if (!isLoading) {
      onRowClick(newsId)
    }
  }, [newsId, isLoading])

  const onClickBookmark = (e, rowId, val) => {
    e.stopPropagation()
    if (!val) {
      dispatch(
        postBookmark({
          paramDelete: { NewsId: rowId },
          paramsGetTable: { ...params },
        }),
      )
    } else {
      dispatch(
        deleteBookmark({
          paramDelete: { NewsId: rowId },
          paramsGetTable: { ...params },
        }),
      )
    }
  }

  const schemaTable = HEADER_TITLE.map((item, index) => {
    const title = item.label
    const colId = `${item.key}`
    const result = {
      title,
      colId,
      isI18n: true,
      tdStyle: { cursor: 'pointer' },
    }
    if (index === 0) {
      return {
        ...result,
        render: (val, { rowId }) => {
          return (
            <BookmarkCell
              val={val}
              rowId={rowId}
              onClickBookmark={onClickBookmark}
            />
          )
        },
      }
    }
    if (index === 1) {
      return {
        ...result,
        render: (val, { rowId }) => {
          return <StatusCell val={val} rowId={rowId} />
        },
      }
    }
    if (index === 2 || index === 4) {
      return {
        ...result,
        render: (val) => (
          <TextEllipsisStaticLine
            zIndexTooltip={7}
            text={val}
            isI18n={false}
            lineClamp={1}
            tooltipMaxWidthByContent={true}
          />
        ),
      }
    }
    if (index === 3) {
      return {
        ...result,
        render: (val) => <EventCell val={val} />,
      }
    }
    if (index === 5) {
      return {
        ...result,
        render: (val) => formatDateTime(val, FORMAT.DATE, locale),
      }
    }
    return {
      ...result,
      render: (val) => val,
    }
  })

  const setPageSize = (value) => {
    dispatch(changePageSize(value))
  }

  const setPage = (value) => {
    dispatch(changePage(value))
  }
  const handleSearchText = (e) => {
    handleSearch(e)
  }

  const handleSearch = useCallback(
    debounce((textSearch) => {
      if (textSearch.length > 1) {
        dispatch(changeSearchText(textSearch))
      }

      if (!textSearch) {
        dispatch(changeSearchText(undefined))
      }
    }, 500),
    [],
  )

  const renderTopComponent = () => {
    return (
      <div className="dFlex justifyContentBetween alignItemsCenter pb2">
        <Input
          placeHolder="newsAndSocial.PLACE_HOLDER_SEARCH"
          onChange={(e) => handleSearchText(e.target.value)}
          style={{ fontSize: 12, minWidth: 360 }}
        />
      </div>
    )
  }

  const renderBottomComponent = () => {
    return (
      <Translate
        style={{
          fontStyle: 'italic',
          paddingTop: 8,
        }}
        value="newsAndSocial.DISCLAIMER"
      />
    )
  }

  return (
    <div className="flexColumn h100">
      {renderTopComponent()}
      <div
        className="flex1"
        style={{
          paddingBottom: 12,
          borderBottomColor: '#E5E5E8',
          borderBottomStyle: 'solid',
          borderBottomWidth: 1,
        }}
      >
        <Table
          ids={ids}
          isSetBgTableWithNoData
          isCenterNoData={false}
          getDataFromRedux={selectTableItem}
          noDataText="newsAndSocial.NO_DATA"
          renderTBody={() => (
            <FilterTableBody
              onRowClick={onRowClick}
              ids={ids}
              getDataFromRedux={selectTableItem}
              schema={schemaTable}
            />
          )}
          isLoading={isLoading}
          isNoDataWithHead={true}
          isDisplayScroll={false}
          schema={schemaTable}
          renderPagination={() => (
            <Pagination
              page={page}
              pageSize={pageSize}
              total={totalRecords}
              setPageSize={setPageSize}
              setPage={setPage}
              className="pt2"
            />
          )}
        />
      </div>
      {renderBottomComponent()}
    </div>
  )
}
